import { observable, computed, makeObservable } from 'mobx'
import { arrayMove } from 'react-sortable-hoc'
import moment from 'moment-timezone'
import { Page } from './Page'
import SkipLogic from './SkipLogic'
import { v4 as uuidv4 } from 'uuid'
import * as R from 'ramda'
class SurveyLang {
    constructor({ id, text }) {
        this.id = id
        this.text = text ? text.replace(/&amp;/g, '&') : text
    }

    toJSON() {
        return {
            id: this.id,
            text: this.text,
        }
    }
}

const surveyStatuses = {
    created: 'Created',
    deleted: 'Deleted',
    archived: 'Archived',
    closed: 'Closed',
}

class IndustrySpecificInfo {
    /**
     * @param {Object} options
     * @param {ProductType} options.productType
     * @param {String} productName
     */

    @observable startDate

    @observable productType

    constructor({
        productType,
        productName,
        startDate,
        endDate,
        journeyId,
    }) {
        makeObservable(this)
        this.productType = productType
        this.productName = productName ? productName.replace(/&amp;/g, '&') : productName
        this.startDate = startDate ? moment(startDate) : undefined
        this.endDate = endDate ? moment(endDate) : undefined
        this.journeyId = journeyId
    }

    toJSON() {
        return {
            industry: this.industry,
            productType: this.productType,
            productName: this.productName,
            startDate: this.startDate ? moment(this.startDate).tz('UTC', true).format() : undefined,
            endDate: this.endDate ? moment(this.endDate).tz('UTC', true).format() : undefined,
            journeyId: this.journeyId,
        }
    }
}

const industries = {
    EventsIndustry: 'EV',
    VisitorAttractionIndustry: 'VA',
    PublicationIndustry: 'PU',
    CorporateEvents: 'CE',
    EXPO2020: 'EXPO',
    Dentistry: 'DENT',
    Venue: 'VENU'
}

class EventsIndustryInfo extends IndustrySpecificInfo {

    constructor({
        venueId,
        numVisitors,
        numExhibitors,
        eventType,
        eventContains,
        ...industryInfo
    }) {
        super(industryInfo)
        this.industry = industries.EventsIndustry
        this.venueId = venueId
        this.numVisitors = numVisitors
        this.numExhibitors = numExhibitors
        this.eventType = eventType
        this.eventContains = (eventContains && eventContains !== '')
            ? eventContains.split(',')
            : []

    }

    toJSON() {
        let eventContains = this.eventContains.filter((a) => a !== '')
        if (eventContains.length > 0) {
            eventContains = eventContains.join(',')
        } else {
            eventContains = undefined
        }
        return Object.assign(
            {},
            super.toJSON(),
            {
                venueId: this.venueId,
                numVisitors: this.numVisitors,
                numExhibitors: this.numExhibitors,
                eventType: this.eventType,
                eventContains,
            },
        )
    }
}

class CorporateEventsInfo extends IndustrySpecificInfo {
    constructor({
        venue,
        numVisitors,
        eventType,
        // numExhibitors,
        ...industryInfo
    }) {
        super(industryInfo)
        this.industry = industries.CorporateEvents
        this.venue = venue
        this.eventType = eventType
        this.numVisitors = numVisitors
        // this.numExhibitors = numExhibitors
    }

    toJSON() {
        let city = ''
        let venue = ''
        let state = ''
        if (this.venue && typeof this.venue !== 'string') {
            city = this.venue.city.value !== -1
                ? this.venue.city.label
                : this.venue.otherCity
            venue = this.venue.venue.value !== -1
                ? this.venue.venue.label
                : this.venue.otherVenue
            state = R.hasPath(['state', 'label'], this.venue)
                ? this.venue.state.label
                : ''
        }
        return Object.assign(
            {},
            super.toJSON(),
            {
                venue: (this.venue && typeof this.venue !== 'string') ? `${venue}|${city}|${this.venue.country.label}|${state}` : this.venue,
                numVisitors: this.numVisitors,
                eventType: this.eventType
            //     numExhibitors: this.numExhibitors,
            },
        )
    }
}

class DentistryInfo extends IndustrySpecificInfo {
    constructor({
        venue,
        numVisitors,
        // numExhibitors,
        ...industryInfo
    }) {
        super(industryInfo)
        this.industry = industries.Dentistry
        this.venue = venue
        this.numVisitors = numVisitors
        // this.numExhibitors = numExhibitors
    }

    toJSON() {
        let city = ''
        let venue = ''
        if (this.venue && typeof this.venue !== 'string') {
            city = this.venue.city.value !== -1
                ? this.venue.city.label
                : this.venue.otherCity
            venue = this.venue.venue.value !== -1
                ? this.venue.venue.label
                : this.venue.otherVenue
        }
        return Object.assign(
            {},
            super.toJSON(),
            {
                venue: (this.venue && typeof this.venue !== 'string') ? `${venue}|${city}|${this.venue.country.label}` : this.venue,
                numVisitors: this.numVisitors,
            //     numExhibitors: this.numExhibitors,
            },
        )
    }
}

class VisitorAttractionInfo extends IndustrySpecificInfo {
    constructor({ ...industryInfo }) {
        super(industryInfo)
        this.industry = industries.VisitorAttractionIndustry
    }

    toJSON() {
        return super.toJSON()
    }
}

class PublicationInfo extends IndustrySpecificInfo {
    constructor({ ...industryInfo }) {
        super(industryInfo)
        this.industry = industries.PublicationIndustry
    }

    toJSON() {
        return super.toJSON()
    }
}

class EXPO2020Info extends IndustrySpecificInfo {
    constructor({ ...industryInfo }) {
        super(industryInfo)
        this.industry = industries.EXPO2020
    }

    toJSON() {
        return super.toJSON()
    }
}
class VenueInfo extends IndustrySpecificInfo {
    constructor({ ...industryInfo }) {
        super(industryInfo)
        this.industry = industries.Venue
    }

    toJSON() {
        return super.toJSON()
    }
}

class ProductInfo {
    constructor({ productId, industrySpecificInfo }) {
        this.productId = productId
        switch (industrySpecificInfo.industry) {
            case industries.EventsIndustry:
                this.industrySpecificInfo = new EventsIndustryInfo(industrySpecificInfo)
                break
            case industries.CorporateEvents:
                this.industrySpecificInfo = new CorporateEventsInfo(industrySpecificInfo)
                break
            case industries.VisitorAttractionIndustry:
                this.industrySpecificInfo = new VisitorAttractionInfo(industrySpecificInfo)
                break
            case industries.PublicationIndustry:
                this.industrySpecificInfo = new PublicationInfo(industrySpecificInfo)
                break
            case industries.EXPO2020:
                this.industrySpecificInfo = new EXPO2020Info(industrySpecificInfo)
                break
            case industries.Dentistry:
                this.industrySpecificInfo = new DentistryInfo(industrySpecificInfo)
                break
            case industries.Venue:
                this.industrySpecificInfo = new VenueInfo(industrySpecificInfo)
                break
            default:
        }
    }

    toJSON() {
        return {
            productId: this.productId,
            industrySpecificInfo: this.industrySpecificInfo.toJSON(),
        }
    }
}

class SurveyTaxonomy {
    @observable respondentType

    @observable surveyType

    @observable templateLicenceType

    constructor({ surveyType, respondentType, templateLicenceType }) {
        makeObservable(this)
        this.surveyType = surveyType
        this.respondentType = respondentType
        this.templateLicenceType = templateLicenceType
    }

    toJSON() {
        return {
            surveyType: this.surveyType,
            respondentType: this.respondentType,
            templateLicenceType: this.templateLicenceType,
        }
    }
}

class SurveyCreationInfo {
    constructor({
        createdTime,
        createdBy,
        copyOf,
    }) {
        this.createdTime = createdTime
        this.createdBy = createdBy
        this.copyOf = copyOf
    }

    toJSON() {
        return {
            createdTime: this.createdTime,
            createdBy: this.createdBy,
            copyOf: this.copyOf,
        }
    }
}

class SurveyDisplaySettings {
    constructor({
        disableMobileDisplayMode = false,
        bannerUrl = '',
        styling = '',
        showPreviewMessage = true,
        showBackButton = true,
        showProgressBar = true,
        backgroundUrl = undefined,
        customStyling = undefined,
        showBreadCrumbs = false,
    }) {
        this.disableMobileDisplayMode = disableMobileDisplayMode
        this.bannerUrl = bannerUrl
        this.styling = styling
        this.showPreviewMessage = showPreviewMessage
        this.showBackButton = showBackButton
        this.showProgressBar = showProgressBar
        this.backgroundUrl = backgroundUrl
        this.customStyling = customStyling
        this.showBreadCrumbs = showBreadCrumbs
    }

    toJSON() {
        return {
            disableMobileDisplayMode: this.disableMobileDisplayMode,
            bannerUrl: this.bannerUrl,
            styling: this.styling,
            showPreviewMessage: this.showPreviewMessage,
            showBackButton: this.showBackButton,
            showProgressBar: this.showProgressBar,
            backgroundUrl: this.backgroundUrl,
            customStyling: this.customStyling,
            showBreadCrumbs: this.showBreadCrumbs,
        }
    }
}

class KioskSettings {
    constructor({
        showRestartButtonAfterTime = 0,
        showRestartPopupAfterTime = 0,
        redirectAfterTime = 0,
        redirectAfterTimeOnCompletion = 0,
        showChooseLanguagePage = false,
    }) {
        this.showRestartButtonAfterTime = showRestartButtonAfterTime
        this.showRestartPopupAfterTime = showRestartPopupAfterTime
        this.redirectAfterTime = redirectAfterTime
        this.redirectAfterTimeOnCompletion = redirectAfterTimeOnCompletion
        this.showChooseLanguagePage = showChooseLanguagePage
    }

    toJSON() {
        return {
            showRestartButtonAfterTime: this.showRestartButtonAfterTime,
            showRestartPopupAfterTime: this.showRestartPopupAfterTime,
            redirectAfterTime: this.redirectAfterTime,
            redirectAfterTimeOnCompletion: this.redirectAfterTimeOnCompletion,
            showChooseLanguagePage: this.showChooseLanguagePage,
        }
    }
}

class AdminConsole {
    constructor({
        showLoginOnLoad = false,
        password = 'apples',
        kioskSettings = {},
    }) {
        this.showLoginOnLoad = showLoginOnLoad
        this.password = password
        this.kioskSettings = kioskSettings instanceof KioskSettings
            ? kioskSettings : new KioskSettings(kioskSettings)
    }

    toJSON() {
        return {
            showLoginOnLoad: this.showLoginOnLoad,
            password: this.password,
            kioskSettings: this.kioskSettings.toJSON(),
        }
    }
}

class SurveyMetadata {
    @observable closeDate

    @observable adminConsoleEnabled

    @observable status

    @observable isTemplatePublished

    constructor({
        status = surveyStatuses.created,
        clientId,
        productInfo,
        taxonomy,
        creationInfo,
        locales,
        defaultLocale,
        closeDate,
        adminConsoleEnabled = false,
        adminConsole = {},
        prePopulated = false,
        displaySettings,
        branches,
        defaultYrOnYrSurveyId,
        isTest = false,
        cacheLocation = 'default',
        isTemplate = false,
        excludeFromBenchmarks = false,
        allowQuotas,
        isPrimary,
        isTemplatePublished,
        isRapidSurvey,
        isSurveyAiSummaryEnabled,
        isSalesIntelligenceEnabled,
        isParent,
        parentSurveyId,
        sIObjectivesQuestionId
    }) {
        makeObservable(this)
        this.status = status
        this.clientId = clientId
        this.productInfo = productInfo instanceof ProductInfo
            ? productInfo : new ProductInfo(productInfo)

        this.taxonomy = taxonomy instanceof SurveyTaxonomy
            ? taxonomy : new SurveyTaxonomy(taxonomy)

        this.creationInfo = creationInfo instanceof SurveyCreationInfo
            ? creationInfo : new SurveyCreationInfo(creationInfo)

        this.locales = locales
        this.defaultLocale = defaultLocale
        this.closeDate = moment(closeDate)

        this.adminConsoleEnabled = adminConsoleEnabled
        this.adminConsole = adminConsole instanceof AdminConsole ? adminConsole : new AdminConsole(adminConsole)
        this.prePopulated = prePopulated
        this.allowQuotas = allowQuotas
        this.displaySettings = displaySettings instanceof SurveyDisplaySettings ? displaySettings : new SurveyDisplaySettings(displaySettings)

        this.branches = branches
        this.defaultYrOnYrSurveyId = defaultYrOnYrSurveyId
        this.isTest = isTest
        this.cacheLocation = cacheLocation
        this.isTemplate = isTemplate
        this.excludeFromBenchmarks = excludeFromBenchmarks
        this.isTemplatePublished = isTemplatePublished ?? false
        if (isPrimary === null || isPrimary === undefined) {
            this.isPrimary = this.taxonomy.surveyType === 'GE' ? false : true
        }else {
            this.isPrimary = isPrimary
        }
        this.isRapidSurvey = isRapidSurvey
        this.isSurveyAiSummaryEnabled = isSurveyAiSummaryEnabled
        this.sIObjectivesQuestionId = sIObjectivesQuestionId
        this.isSalesIntelligenceEnabled = isSalesIntelligenceEnabled
        this.isParent = isParent
        this.parentSurveyId = parentSurveyId
    }


    addLanguage(locale) {
        if (this.locales.includes(locale)) return
        this.locales.push(locale)
    }

    removeLanguage(locale) {
        const localeIndex = this.locales.indexOf(locale)
        if (localeIndex > -1) {
            this.locales.splice(localeIndex, 1)
        }
    }

    toJSON() {
        return {
            status: this.status,
            clientId: this.clientId,
            productInfo: this.productInfo.toJSON(),
            taxonomy: this.taxonomy,
            creationInfo: this.creationInfo,
            locales: this.locales,
            defaultLocale: this.defaultLocale,
            closeDate: this.closeDate,
            adminConsoleEnabled: this.adminConsoleEnabled,
            adminConsole: this.adminConsole.toJSON(),
            prePopulated: this.prePopulated,
            displaySettings: this.displaySettings,
            branches: this.branches,
            defaultYrOnYrSurveyId: this.defaultYrOnYrSurveyId,
            isTest: this.isTest,
            cacheLocation: this.cacheLocation,
            isTemplate: this.isTemplate,
            excludeFromBenchmarks: this.excludeFromBenchmarks,
            isPrimary : this.isPrimary,
        }
    }
}

class LocalizedSurveyContent {
    constructor({ title = {}, welcomeContent = {} }) {
        this.title = title instanceof Map
            ? title : new Map(Object.entries(title).map(([locale, surveyLang]) => (
                [locale, (surveyLang instanceof SurveyLang
                    ? surveyLang : new SurveyLang(surveyLang))]
            )))

        this.welcomeContent = welcomeContent instanceof Map
            ? welcomeContent : new Map(Object.entries(welcomeContent)
                .map(([locale, surveyLang]) => (
                    [locale, (surveyLang instanceof SurveyLang
                        ? surveyLang : new SurveyLang(surveyLang))]
                )))
    }

    titleByLocale(locale) {
        return this.title.has(locale)
            ? this.title.get(locale).text
            : ''
    }

    toJSON() {
        return {
            title: this.title,
            welcomeContent: this.welcomeContent,
        }
    }
}

class Survey {
    @observable productSurveys = []

    @observable productSurveysLoading = false

    @observable exploriSurveys = []

    @observable exploriSurveysLoading = false

    @observable pages = []

    @observable skipLogic

    @observable loadingQuestions = true

    @observable num_questions = 0

    @observable pageQuestions = []

    @observable hasMandatoryQuestions = false

    @observable closed = false

    @observable pageBlocks = []

    @observable pageIndexChangesArr = []

    @observable questionLibrary

    @observable mergefields
    
    @observable mergeFieldoptions

    @observable mandatoryPopupShown = false

    @observable rapidSurveyRemainingQuestions = 3

    constructor({
        id,
        content,
        pages,
        metadata,
        skipLogic,
        publicId,
        pageBlocks,
        mergefields
    }) {
        makeObservable(this)
        this.id = id
        this.publicId = publicId
        this.content = content instanceof LocalizedSurveyContent
            ? content : new LocalizedSurveyContent(content)

        this.pages = Array.isArray(pages)
            ? pages.map(page => (page instanceof Page ? page : new Page(page))) : []

        this.metadata = metadata instanceof SurveyMetadata ? metadata : new SurveyMetadata(metadata)

        if (this.id && this.metadata.closeDate.isBefore(moment())) {
            this.metadata.status = 'Closed'
            this.closed = true
        }

        this.skipLogic = Array.isArray(skipLogic)
            ? skipLogic.map(logic => (logic instanceof SkipLogic
                ? logic : new SkipLogic(logic))) : []

        this.title = this.content.titleByLocale(this.metadata.defaultLocale)

        this.mergefields = mergefields

        this.pages.forEach((page) => {
            if (page.pageType === 'ST') {
                page.questions.forEach(pq => this.pageQuestions.push(pq.questionId))
            }
        })

        this.pageBlocks = pageBlocks ? pageBlocks : []
    }

    @computed get standardPages() {
        return this.pages.reduce((pages, page) => {
            if (page.pageType === 'ST') {
                pages.push(page)
            }
            return pages
        }, [])
    }

    @computed get exitPages() {
        return this.pages.reduce((pages, page) => {
            if (['EX', 'CO', 'QF'].indexOf(page.pageType) > -1) {
                pages.push(page)
            }
            return pages
        }, [])
    }

    @computed get welcomePage() {
        return {
            pageType: 'welcome',
            id: new Date().getTime(),
        }
    }

    getYear = () => {
        const { metadata } = this
        const { productInfo, closeDate } = metadata
        const { industrySpecificInfo } = productInfo
        const { endDate } = industrySpecificInfo
        return new Date(endDate || closeDate).getFullYear()
    }
    
    getPageBlocks = () => this.pageBlocks

    createPageBlock = (store) => {
        // TODO: TH improve error handling

        const newPageBlocks = [...this.pageBlocks]
        // const title = new Map()
        // title.set(this.metadata.defaultLocale,  {text: `Page Block ${this.pageBlocks.length + 1}`})

        const title = {[this.metadata.defaultLocale] : {text: `Page Block ${this.pageBlocks.length + 1}`} }

        newPageBlocks.push({
            id: uuidv4(),
            isHidden: false,
            pages: [],
            title
          })

          this.updatePageBlocks(store, newPageBlocks)
    }

    deletePageBlock = (store, pageBlockId) => {
        const { survey } = store
    
        let newPages = JSON.parse(JSON.stringify(survey.pages))
        const newPageBlocks = [...this.pageBlocks]
        const pageBlockIndex = newPageBlocks.findIndex(pb => pb.id === pageBlockId)
        const movingPages = newPages.filter(p => newPageBlocks[pageBlockIndex].pages.includes(p.id))
        movingPages.reverse()
        const totalPageBlockPages = this.pageBlocks.flatMap((pb) => pb.pages.slice())
        let inheritanceWarn = false
        let currentPageIndex = null
        let newPageIndex = null

        movingPages.forEach((mp, index) => {
            currentPageIndex = this.standardPages.findIndex((p) => p.id === mp.id)
            newPageIndex = totalPageBlockPages.length - (index + 1)
            this.pageIndexChangesArr.push ({oldIndex:currentPageIndex, newIndex: newPageIndex})
            newPages = arrayMove(newPages, currentPageIndex, newPageIndex)
            newPages.forEach((page, index) => {
                page.priority = index + 1
            })
            inheritanceWarn = inheritanceWarn || this.pageMoveLogicCheck( currentPageIndex, newPageIndex, newPages )
        })
        if (!inheritanceWarn) {
            this.confirmBlockDelete(store,pageBlockIndex)
            return true
        } else {
            return false
        }
    }

    confirmBlockDelete = (store, pageBlockIndex) => {
        const newPageBlocks = [...this.pageBlocks]
        if (this.pageIndexChangesArr.length) {
            this.pageIndexChangesArr.forEach((c) => {
                this.movePage(store, c.oldIndex, c.newIndex, false)
            })
        }
        newPageBlocks.splice(pageBlockIndex, 1)
        this.updatePageBlocks(store, newPageBlocks,'Page Block Deleted')
        this.pageIndexChangesArr = []
    }

    removePagePageBlock  = (store, pageBlockId, pageId) => {
        const newPageBlocks = [...this.pageBlocks]
        const pageBlockIndex = newPageBlocks.findIndex((pb => pb.id == pageBlockId));
        newPageBlocks[pageBlockIndex].pages = newPageBlocks[pageBlockIndex].pages.filter(p => p !== pageId)
        this.updatePageBlocks(store, newPageBlocks)
    }

    addPageToPageBlock = (store, pageBlockId, pageId, prevPageBlockId = false) => {
        const newPageBlocks = [...this.pageBlocks]
        const pageBlockIndex = newPageBlocks.findIndex((pb => pb.id == pageBlockId));
        if (prevPageBlockId) {
            const prevPageBlockIndex = newPageBlocks.findIndex((pb => pb.id == prevPageBlockId));
            newPageBlocks[prevPageBlockIndex].pages = newPageBlocks[prevPageBlockIndex].pages.filter(p => p !== pageId)
        }
        newPageBlocks[pageBlockIndex].pages.push(pageId)   
        this.updatePageBlocks(store, newPageBlocks)
    }

    updatePageBlocks = (store, newPageBlocks, successNotification = 'Page Block Updated') => {
        const endpoint = `/api/survey/${this.id}/pageblocks`
        const body = JSON.stringify({ pageBlocks: newPageBlocks })

        store.http.put(endpoint, body, store.surveyToken)
        .then((res) => {
            this.pageBlocks = newPageBlocks
            store.addNotification(null, successNotification, 'success')
        })
        .catch(e => {
            console.log(e)
        })
    }

    pageMoveLogicCheck = ( from, to, pages ) => {
        // This Will return true if there is logic that will be affected by the move. false if not. 
        // Expects to receive pages in state they will be in after the move
        console.log('pageMoveLogicCheck')
        if (from !== to) {
            const currentPage = pages[from]
            const pageId = currentPage.id.toLowerCase()
            const currentPageQuestionIds = currentPage.questions.map(pq => pq.id.toLowerCase())
            
            const parentIndexes = pages[to].questions.reduce((indexes, pq) => {
                if (pq.parentId) {
                    const parentIndex = pq.getParentPageIndex(this)
                    if (parentIndex >= (to + 1)) {
                        indexes.push(parentIndex)
                    }
                }
                return indexes
            }, [])
            let pageQuestionIds = []
            let childIndexes = []
            if (currentPage.questions.length) {
                pageQuestionIds = currentPage.questions.reduce((ids, pq) => {
                    ids.push(pq.id.toLowerCase())
                    return ids
                }, [])
                childIndexes = pages.reduce((children, page) => {
                    if (page.questions && page.priority <= (to + 1) && page.questions.filter(pq => (pq.parentId && pageQuestionIds.includes(pq.parentId.toLowerCase()))).length) {
                        children.push(page.priority)
                    }
                    return children
                }, [])
            }
            const {
                prevPageIds,
                nextPageIds,
                prevPageQuestionIds,
                nextPageQuestionIds,
            } = pages.reduce((obj, page) => {
                if (page.priority < currentPage.priority) {
                    obj.prevPageIds.push(page.id)
                    obj.prevPageQuestionIds = obj.prevPageQuestionIds.concat(page.questions.map(pq => pq.id.toLowerCase()))
                } else if (page.priority > currentPage.priority && page.pageType === 'ST') {
                    obj.nextPageIds.push(page.id)
                    obj.nextPageQuestionIds = obj.nextPageQuestionIds.concat(page.questions.map(pq => pq.id.toLowerCase()))
                }
                return obj
            }, {
                prevPageIds: [],
                nextPageIds: [],
                prevPageQuestionIds: [],
                nextPageQuestionIds: [],
            })
            let skipToPage = false
            let showIf = false
            if (!parentIndexes.length && !childIndexes.length) {
                skipToPage = this.skipLogic.some((logic) => {
                    const pageQuestionId = R.pathOr(-1, ['triggerGroups', 0, 'children', 0, 'triggers', 0, 'condition', 'pageQuestionId'], logic)
                    return (
                        logic.action.kind === 'PageToPage'
                        && (
                            (logic.action.pageId === pageId && nextPageIds.includes(logic.trigger.condition.pageId))
                                || (logic.trigger.condition.pageId === pageId && prevPageIds.includes(logic.action.pageId))
                        )
                    )
                    || (
                        logic.action.kind === 'SkipToPage'
                        && (
                            (currentPageQuestionIds.includes(pageQuestionId)
                                && prevPageIds.includes(logic.action.pageId))
                            || (
                                pageId === logic.action.pageId
                                    && nextPageQuestionIds.includes(pageQuestionId)
                            )
                        )
                    )
                })
                if (!skipToPage && currentPage.questions.length) {
                    showIf = this.skipLogic.some(logic => (
                        logic.action.kind === 'ShowQuestion'
                            && (
                                (
                                    currentPageQuestionIds.includes(logic.action.pageQuestionId)
                                    && logic.triggerGroups.some(tg => (
                                        tg.children.some(child => (
                                            child.triggers.some(trigger => (
                                                nextPageQuestionIds.includes(trigger.condition.pageQuestionId)
                                            ))
                                        ))
                                    ))
                                ) || (
                                    prevPageQuestionIds.includes(logic.action.pageQuestionId)
                                    && logic.triggerGroups.some(tg => (
                                        tg.children.some(child => (
                                            child.triggers.some(trigger => (
                                                currentPageQuestionIds.includes(trigger.condition.pageQuestionId)
                                            ))
                                        ))
                                    ))
                                )
                            )
                    ))
                }
            }
            if (parentIndexes.length || childIndexes.length || skipToPage || showIf) {
                return true
            } else {
                return false
            }
        }
    }

    replaceMergeFields = (text, store) => {
        const { surveyLocale, productLocales } = store
        let replacedText = text
        try {
            let eventName = this.getProductName(productLocales)
            if (Object.hasOwnProperty.call(productLocales, surveyLocale)) {
                eventName = productLocales[surveyLocale]
            }
            replacedText = text.replace(/\[eventname\]/gi, eventName)
        } catch (e) {
            console.error(e)
        }
        return replacedText
    }

    getProductName = (productLocales) => {
        return (productLocales && productLocales.default) || this.metadata.productInfo.industrySpecificInfo.productName
    }

    inheritLogicExists(parentId) {
        console.log(`inheritLogicExists ${parentId}`)
        if (!parentId) return false
        return this.skipLogic.some(logic => (
            ['SkipToPage', 'ShowQuestion'].includes(logic.action.kind)
                && logic.triggerGroups.some(tg => (
                    tg.children.some(child => (
                        child.triggers.some(trigger => (
                            trigger.condition.inheritedPageQuestionId === parentId
                        ))
                    ))
                ))
        ))
    }

    getPageIndex = (pageId) => {
        return this.pages.findIndex((page) => page.id === pageId)
    }

    movePage = (store, oldIndex, newIndex, soft) => {
        const that = this
        const new_arr = arrayMove(this.pages, oldIndex, newIndex)
        let response = null
        this.pages = new_arr
        const endpoint = "/api/survey/" + this.id + "/page/" + that.pages[newIndex].id + "/move"
        const body = JSON.stringify({
            toPos: (newIndex + 1),
        })
        if (soft) {
            response = {
                endpoint,
                body,
            }
            this.pages.forEach((page, index) => {
                page.priority = index + 1
            })
        } else {
            store.http.put(endpoint, body, store.surveyToken)
                .then((res) => {
                    console.log("page sorted " + oldIndex + ", " + newIndex)
                    this.pages.forEach((page, index) => {
                        page.priority = index + 1
                    })
                    store.addNotification(null, 'Page moved', 'success')
                    this.refreshSkipLogic(store, false)
                    // this.pages[newIndex].validateSkipToPage()
                    // that.checkQuestionInheritance()
                })
                .catch(e => console.log(e))
        }
        return response
    }

    saveMove = (store, body, endpoint) => {
        return store.http.put(endpoint, body, store.surveyToken)
            .then((res) => {
                this.pages.forEach((page, index) => {
                    page.priority = index + 1
                })
                store.addNotification(null, 'Page moved', 'success')
                this.refreshSkipLogic(store, false)
                return true
                    // this.pages[newIndex].validateSkipToPage()
                // that.checkQuestionInheritance()
            })
            .catch((e) => {
                console.log(e)
                return false
            })
    }

    getPrintLink = (useLatestUI) => {
        return `${this.getPreviewLink(true, useLatestUI)}&langcode=${this.metadata.defaultLocale}&print=1`
        //"#surveyUri#?surveyUuid=#rc.surveyId#&langcode=#langcode#&print=1"
        //"/survey/" + this.id + "/print/?langcode=" + store.surveyLocale
    }

    getInterceptSrc = (campaignUuid, token) => {
        return `<script type="text/javascript" src="https://surveys.explori.com/v2/embed.js"></script>
        <script type="text/javascript">
            exploriIntercept('${campaignUuid}', '${token}');
        </script>`
    }

    getEmbedSrc = (campaignUuid, langCode, useLatestUI, token) => {
        const link = this.getCampaignLink(campaignUuid, langCode, useLatestUI)
        return `<iframe src="${link}&token=${token}" frameBorder="0"/>`
    }

    getCampaignLink = (campaignUuid, langCode, useLatestUI) => {
        let link = this.getPreviewLink(false, useLatestUI)
        link += `&campaignuuid=${campaignUuid}`
        if (langCode) {
            link += `&langcode=${langCode}`
        }

        return link
    }

    static getPreviewLinkNew = (noCache, useLatestUI, locale, publicId) => {
        const { protocol } = window.location
        let link = window.location.hostname.split('.')
        if (window.location.hostname === 'dev.admin.staging.explori.com') {
            link = 'surveys.staging.explori.com'.split('.')
        } else if (link.includes('staging') && link[0] !== 'admin') {
            link.splice(0, 2, 'surveys')
        } else if (link[0].match(/tasks/)) {
            link = 'surveys.explori.com'.split('.')
        } else {
            link[0] = 'surveys'
        }

        return `${protocol}//${link.join('.')}/?surveyUuid=${publicId}${!R.isNil(locale) ? `&langcode=${locale}` : ''}${noCache ? '&noCache=1' : ''}${useLatestUI ? '&version=v2' : ''}`
    }

    getPreviewLink = (noCache, useLatestUI, locale) => {
        return Survey.getPreviewLinkNew(noCache, useLatestUI, locale, this.publicId)
    }

    getUserLink = (userId, useLatestUI) => {
        const { protocol } = window.location
        let link = window.location.hostname.split('.')
        if (window.location.hostname === 'dev.admin.staging.explori.com') {
            link = 'surveys.staging.explori.com'.split('.')
        } else if (link.includes('staging') && link[0] !== 'admin') {
            link.splice(0, 2, 'surveys')
         } else if (link[0].match(/tasks/)) {
            link = 'surveys.explori.com'.split('.')
        } else {
            link[0] = 'surveys'
        }
        return `${protocol}//${link.join('.')}/?surveyUuid=${this.publicId}&surveyUserUUID=${userId}${(useLatestUI ? '&version=v2' : '')}`
    }

    findPageBlocksByPageId = (pageId) => (
        this.pageBlocks.filter(({ pages }) => (
            pages.includes(pageId)
        ))
    )

    removePage = (store, page) => {
        console.log('removePage')
        console.log(page.getSkipLogic(store))
        // page.getSkipLogic(store).forEach(skipLogic => skipLogic.remove(store))
        const questionCount = page.questions ? page.questions.length : 0
        // if (page.questions) {
        //     page.questions.forEach(question => question.filterSkipLogic(store).forEach(skipLogic => skipLogic.remove(store)))
        // }
        const linkedPageBlocks = this.findPageBlocksByPageId(page.id)
        store.http.delete(`/api/survey/${this.id}/page/${page.id}`, store.surveyToken)
            .then((response) => {
                linkedPageBlocks.forEach(({ id }) => {
                    this.removePagePageBlock(store, id, page.id)
                })
                store.survey.num_questions -= questionCount
                // console.log(this.pages.map(page => page.priority))
                // console.log(page.priority)
                this.pages.splice((page.priority - 1), 1)
                this.pages.forEach((page, index) => {
                    const priority = index + 1
                    page.priority = priority
                })
                this.refreshSkipLogic(store)
                store.addNotification(null, 'Page removed', 'success')
            })
            .catch((e) => {
                console.log(e)
                store.addNotification(null, `Error removing page: ${e.message}`, 'error')
            })
    }

    refreshSkipLogic = (store, hideNotification) => {
        store.http.get(`/api/survey/${store.survey.id}`, store.surveyToken)
            .then((survey) => {
                const { skipLogic, pages } = survey
                const oldSkipLogic = JSON.stringify(this.skipLogic.toJSON())
                this.pages.forEach((p, i) => {
                    if (p.questions) {
                        const newPage = pages[i]
                        p.questions.forEach((pq, j) => {
                            const newPQ = newPage.questions[j]
                            pq.skipLogic = Array.isArray(newPQ.skipLogic)
                                ? newPQ.skipLogic.map(logic => (
                                    logic instanceof SkipLogic
                                        ? logic
                                        : new SkipLogic(logic)
                                    )
                                )
                                : []
                        })
                    }
                })
                this.skipLogic = Array.isArray(skipLogic)
                    ? skipLogic.map(logic => (logic instanceof SkipLogic
                        ? logic : new SkipLogic(logic))) : []
                if (JSON.stringify(this.skipLogic.toJSON()) !== oldSkipLogic) {
                    // console.log(oldSkipLogic)
                    // console.log(JSON.stringify(this.skipLogic.toJSON(), null, 4))
                    if (!hideNotification) {
                        store.addNotification(null, `Skiplogic updated`, 'success')
                    }
                }
            })
            .catch((e) => {
                if (!hideNotification) {
                    store.addNotification(null, `Error refreshing skiplogic`, 'error')
                }
                console.log(e)
            })
    }

    addScreeningExitPage = (store) => {
        const priority = this.pages.length + 1
        const page = new Page({
            pageType: 'EX',
            priority,
            title: 'Screening Exit Page',
        })
        const content = JSON.stringify({
            atPos: priority,
            pageType: 'EX',
            title: 'Screening Exit Page',
        })

        store.http.post(`/api/survey/${this.id}/page`, content, store.surveyToken)
            .then((response) => {
                console.log(response)
                page.id = response.newPageId
                this.pages.push(page)
                store.addNotification(null, 'Screening exit page added', 'success')
            })
            .catch((e) => {
                console.log(e)
                store.addNotification(null, `Error adding screening exit page: ${e.message}`, 'error')
            })
    }

    addCompletionPage = (store) => {
        const priority = this.pages.length + 1
        const page = new Page({
            pageType: 'CO',
            priority,
            title: 'Completion Exit Page',
        })
        const content = JSON.stringify({
            atPos: priority,
            pageType: 'CO',
            title: 'Completion Exit Page',
        })

        store.http.post(`/api/survey/${this.id}/page`, content, store.surveyToken)
            .then((response) => {
                console.log(response)
                page.id = response.newPageId
                this.pages.push(page)
                store.addNotification(null, 'Completion exit page added', 'success')
            })
            .catch((e) => {
                console.log(e)
                store.addNotification(null, `Error adding completion exit page: ${e.message}`, 'error')
            })
    }

    addQuotaFullPage = (store) => {
        const priority = this.pages.length + 1
        const page = new Page({
            pageType: 'QF',
            priority,
            title: 'Quota Full Page',
        })
        const content = JSON.stringify({
            atPos: priority,
            pageType: 'QF',
            title: 'Quota Full Page',
        })

        store.http.post(`/api/survey/${this.id}/page`, content, store.surveyToken)
            .then((response) => {
                console.log(response)
                page.id = response.newPageId
                this.pages.push(page)
                store.addNotification(null, 'Quota full page added', 'success')
            })
            .catch((e) => {
                console.log(e)
                store.addNotification(null, `Error adding quota full page: ${e.message}`, 'error')
            })
    }

    addPage = (store, priority, pageBlockId = false) => {
        const page = new Page({
            pageType: 'ST',
            priority,
            title: 'New Page',
        })
        const content = JSON.stringify({
            atPos: priority,
            pageType: 'ST',
            title: 'New Page',
        })
        store.http.post(`/api/survey/${this.id}/page`, content, store.surveyToken)
            .then((response) => {
                console.log(response)
                page.id = response.newPageId
                this.pages.splice((priority - 1), 0, page)
                this.pages.forEach((page, index) => {
                    const priority = index + 1
                    page.priority = priority
                })
                if (pageBlockId) {
                    this.addPageToPageBlock(store, pageBlockId, page.id)
                }
                store.addNotification(null, 'Page added', 'success')
            })
            .catch((e) => {
                console.log(e)
                store.addNotification(null, `Error adding page: ${e.message}`, 'error')
            })
    }

    showEventOptions = () => {
        const { respondentType, surveyType } = this.metadata.taxonomy
        const industryCode = this.metadata.productInfo.industrySpecificInfo.industry
        return industryCode === 'EV'
            && respondentType !== ''
            && surveyType !== ''
            && respondentType !== 'NN'
            && surveyType !== 'GE'
    }

    validateDate = (date) => {
        const dateString = moment(date).format('MM/DD/YYYY')
        let valid = !!date && dateString.match(/\d{2}\/\d{2}\/\d{4}/)
        if (valid) {
            const parts = dateString.split('/')
            valid = valid && parseInt(parts[0], 10) <= 12
            valid = valid && parseInt(parts[1], 10) <= 31
        }
        return valid
    }

    showPeriod = () => {
        const { respondentType, surveyType } = this.metadata.taxonomy
        const industryCode = this.metadata.productInfo.industrySpecificInfo.industry
        return (industryCode === 'PU' || industryCode === 'VA')
            && respondentType !== ''
            && surveyType !== ''
            && respondentType !== 'NN'
            && surveyType !== 'GE'
    }

    townDisabled = () => {
        return this.closed || typeof this.country !== 'object'
    }

    venueDisabled = () => {
        return this.closed || typeof this.town !== 'object'
    }

    surveyTypeDisabled = () => {
        return this.metadata.taxonomy.respondentType === 'NN'
    }

    copySkipLogic(logic) {
        return new SkipLogic(logic)
    }

    newShowPage(page) {
        return new SkipLogic({
            action: {
                kind: 'ShowPage',
                pageId: page.id,

            },
            triggerGroups: [{
                children: [],
                interGroupLogicalOperator: 'OR',
            }],
        }, this.store) 
    }

    newShowIf(pageQuestion) {
        const skipLogic = new SkipLogic({
            action: {
                kind: 'ShowQuestion',
                pageQuestionId: pageQuestion.id,

            },
            triggerGroups: [{
                children: [],
                interGroupLogicalOperator: 'OR',
            }],
        }, this.store)
        return skipLogic
    }

    newPageToPage(pageId, targetPageId) {
        const skipLogic = new SkipLogic({
            action: {
                kind: 'PageToPage',
                pageId: targetPageId,
            },
            trigger: {
                condition: {
                    kind: 'CurrentPage',
                    pageId,
                    equalityOperator: 'Eq',
                },
            },
        }, this.store)
        return skipLogic
    }

    newQuotaFullToPage = (targetPageId, quota) => {
        console.log(`newQuotaFullToPage ${targetPageId}, ${quota}`)
        const skipLogic = new SkipLogic({
            action: {
                kind: 'OverallQuotaTopage',
                pageId: targetPageId,
            },
            trigger: {
                condition: {
                    kind: 'Quota',
                    quota,
                    equalityOperator: 'Gte',
                },
            },
        }, this.store)
        return skipLogic
    }

    newQuestionQuotaToPage = (pageQuestionId, targetPageId, quota, equalityOperator) => (
        new SkipLogic({
            action: {
                kind: 'SkipToPage',
                pageId: targetPageId

            },
            triggerGroups: [
                {
                    children: [
                        {
                            triggers: [
                                {
                                    condition: {
                                        kind: 'QuestionQuota',
                                        quota,
                                        equalityOperator,
                                        pageQuestionId,
                                    }
                                }
                            ]
                        }
                    ]
                }
            ]
        })
    )
    
    newQuotaToPage = (pageId, targetPageId, quota, equalityOperator)  => {
        const skipLogic = new SkipLogic({
            action: {
                kind: 'SkipToPage',
                pageId: targetPageId

            },
            triggerGroups: [
                {
                    children: [
                        {
                            triggers: [
                                {
                                    condition: {
                                        kind: 'CurrentPage',
                                        pageId,
                                        equalityOperator: 'Eq',
                                    }
                                },
                                {
                                    condition: {
                                        kind: 'Quota',
                                        quota,
                                        equalityOperator,
                                    }
                                }
                            ]
                        }
                    ]
                }
            ]
        })
        return skipLogic
    }

    newSkipToPage(pageId, pageQuestionId, set1, set2, parentId) {
        const skipLogic = new SkipLogic({
            action: {
                kind: 'SkipToPage',
                pageId: pageId

            },
            triggerGroups: [
                {
                    children: [
                        {
                            triggers: [
                                {
                                    condition: {
                                        kind: 'QuestionOptionExistsInSurveyResponse',
                                        pageQuestionId: pageQuestionId,
                                        set1: set1,
                                        set2: set2 ? set2.id : undefined,
                                        equalityOperator: 'Eq',
                                        inheritedPageQuestionId: parentId,
                                    }
                                }
                            ]
                        }
                    ]
                }
            ]
        })
        return skipLogic
    }

    toJSON() {
        return {
            id: this.id,
            content: this.content.toJSON(),
            pages: this.pages.map(page => page.toJSON()),
            metadata: this.metadata.toJSON(),
            skipLogic: this.skipLogic.map(logic => logic.toJSON()),
        }
    }

    isMaxbi() {
        return R.pathOr(undefined, ['metadata', 'taxonomy', 'surveyType'], this) === 'EM'
    }
}

export {
    Survey,
    SurveyLang,
}
